import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useMainStateStore } from "@/components/main/stores/mainStateStore";
import { useCreateModel } from "@/components/main/utils/models/useCreateModel";
import { VersionChip } from "@/components/main/utils/common/VersionChip";
import {
  ColumnTopSpacer,
  ListBox,
  OVERVIEW_LIST_ROWS,
} from "@/components/main/pages/pageColumn";
import {
  useDuplicateSessionNotice,
  useModelsPolling,
} from "@/components/main/hooks/useModelsPolling";
import { HeroSearch } from "@/components/main/overview/HeroSearch";
import { SpaceFilterRule } from "@/components/main/overview/SpaceFilterRule";
import { OrgInviteBanner } from "@/components/main/organizations/OrgInviteBanner";
import { SectionRule } from "components/common/SectionRule";
import { ModelRows } from "@/components/main/overview/ModelRows";
import { ModelsLoading } from "@/components/main/overview/ModelsLoading";
import { HintRow } from "@/components/main/overview/ListEmptyShell";
import { PinnedColumn } from "@/components/main/overview/PinnedColumn";
import { CreateModelStrip } from "@/components/main/overview/CreateModelStrip";
import { NewModelOverlay } from "@/components/main/overview/NewModelOverlay";
import {
  PINNED_LIMIT,
  selectAll,
  selectPinned,
  selectRecentForDisplay,
  hasLiveModels,
} from "@/components/main/overview/selectors";
import { useCommandK } from "@/components/main/overview/useCommandK";
import {
  glideRows,
  snapshotRows,
  type RowSnapshot,
} from "@/components/main/overview/glideRows";
import {
  spaceColor,
  useOrgContextStore,
} from "@/components/main/stores/orgContextStore";
import { useTabIdentity } from "components/hooks/useTabIdentity";
import { datasetColorLight } from "@/lib/datasetColor";

type StarClick = {
  list: Element;
  row: Element;
  index: number;
  before: RowSnapshot;
};

export const OverviewPage: React.FC = () => {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [showAll, setShowAll] = useState(false);
  const [showAllPinned, setShowAllPinned] = useState(false);
  const { isOnline, modelsInitialized } = useModelsPolling();
  useDuplicateSessionNotice();
  useCommandK(searchInputRef);
  const { freshModelId, createModel } = useCreateModel();
  const paneRef = useRef<HTMLDivElement>(null);
  const listsRef = useRef<HTMLDivElement>(null);
  // Open with the space the card names; `null` is "ask", never a default.
  const [naming, setNaming] = useState<{ spaceId: string | null } | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const newModelIn = (location.state as { newModelIn?: string } | null)
    ?.newModelIn;
  // The org-created page hands its starter space over; consumed once, so a
  // refresh or Back does not reopen the card.
  useEffect(() => {
    if (!newModelIn) return;
    setNaming({ spaceId: newModelIn });
    void navigate(location.pathname, { replace: true, state: null });
  }, [newModelIn, location.pathname, navigate]);

  const availableModels = useMainStateStore((state) => state.availableModels);
  const selectedSpaceId = useOrgContextStore((s) => s.selectedSpaceId);
  const selectedSpace = useOrgContextStore((s) =>
    s.spaces.find((space) => space.space_id === s.selectedSpaceId),
  );
  useTabIdentity(
    selectedSpace?.name,
    selectedSpace && datasetColorLight(spaceColor(selectedSpace)),
  );
  const setSelectedSpace = useOrgContextStore((s) => s.setSelectedSpace);
  // Catalog models belong to no org and have their own page.
  const models = useMemo(
    () => availableModels.filter((m) => !m.is_catalog),
    [availableModels],
  );
  // The rail's space filter narrows every bucket on the page.
  const scoped = selectedSpaceId
    ? models.filter((m) => m.space_id === selectedSpaceId)
    : models;

  // Pinned lives on All spaces only; a space's Recent takes the full width.
  const allPinned = selectedSpaceId ? [] : selectPinned(models, Infinity);
  if (showAllPinned && allPinned.length <= PINNED_LIMIT)
    setShowAllPinned(false);
  const pinned = showAllPinned ? allPinned : allPinned.slice(0, PINNED_LIMIT);
  const pinnedFolded = allPinned.length === 0 || showAll;
  // Falls back to all models when nothing is recent or pinned yet, so a first
  // login with only shared models still surfaces them here (not an empty hint).
  const recent = selectRecentForDisplay(scoped, undefined, undefined, pinned);
  const all = selectAll(scoped);

  // A star click can take its row out of its list (an unpin in Pinned); focus
  // then goes to the row that took its place, never to the page body.
  const starClick = useRef<StarClick | null>(null);
  const noteStarClick = (e: React.MouseEvent) => {
    const row = (e.target as Element)
      .closest("[data-star]")
      ?.closest("[data-model-id]");
    const list = row?.closest("[data-overview-list]");
    if (!row || !list) return;
    const rows = Array.from(list.querySelectorAll("[data-model-id]"));
    starClick.current = {
      list,
      row,
      index: rows.indexOf(row),
      before: snapshotRows(e.currentTarget),
    };
  };
  useLayoutEffect(() => {
    const click = starClick.current;
    starClick.current = null;
    if (!click) return;
    if (paneRef.current) glideRows(click.before, paneRef.current);
    if (click.row.isConnected) return;
    if (document.activeElement !== document.body) return;
    const stars = (root: Element | null) =>
      Array.from(
        root?.querySelectorAll<HTMLElement>("[data-star]") ?? [],
      ).filter((star) => !star.closest("[inert]"));
    const here = stars(click.list);
    (
      here[Math.min(click.index, here.length - 1)] ??
      stars(listsRef.current)[0] ??
      searchInputRef.current
    )?.focus();
  });

  // Zero models renders the same shell as the populated overview — Recent
  // carries its hint and the create strip pulses its New model button, so the
  // layout never jumps once the first model lands.
  return (
    <div
      ref={paneRef}
      className="relative flex flex-col items-center w-full h-full overflow-y-auto px-6"
      style={{
        fontSize: "var(--font-size)",
        opacity: isOnline ? 1 : 0.55,
        transition: "opacity var(--duration-short) var(--ease-move)",
      }}
    >
      <ColumnTopSpacer />
      <div className="w-full" style={{ maxWidth: "45rem" }}>
        <HeroSearch inputRef={searchInputRef} />
        <OrgInviteBanner />
        {/* 32 above the head, 16 below: it names the lists, so it sits nearer
            them than to the search. */}
        <div className="mt-8">
          <SpaceFilterRule
            spaceId={selectedSpaceId}
            onSelect={setSelectedSpace}
          />
        </div>
        {/* Whether Pinned shows decides the columns, so nothing is laid out
            before the list lands — a late pin would slide them apart. */}
        {!modelsInitialized ? (
          <div ref={listsRef} data-naming-anchor className="mt-4">
            <ModelsLoading isOnline={isOnline} label="Loading models…" />
          </div>
        ) : (
          <div
            ref={listsRef}
            data-naming-anchor
            className="overview-cols mt-4"
            style={{
              gridTemplateColumns: pinnedFolded
                ? "0fr 1fr"
                : showAllPinned
                  ? "1fr 0fr"
                  : "1fr 1fr",
              columnGap:
                pinnedFolded || showAllPinned ? "0px" : "var(--spacing-3xl)",
            }}
            onClickCapture={noteStarClick}
          >
            <div
              className="overview-fold"
              style={{ opacity: pinnedFolded ? 0 : 1 }}
              aria-hidden={pinnedFolded}
              inert={pinnedFolded}
            >
              <PinnedColumn
                models={pinned}
                total={allPinned.length}
                expanded={showAllPinned}
                onToggle={() => {
                  setShowAllPinned((v) => !v);
                  setShowAll(false);
                }}
                testId={showAllPinned ? "all-pinned-list" : undefined}
              />
            </div>
            <div
              className="overview-fold"
              style={{ opacity: showAllPinned ? 0 : 1 }}
              aria-hidden={showAllPinned}
              inert={showAllPinned}
            >
              <SectionRule
                inset
                icon={
                  showAll ? (
                    <DashboardOutlinedIcon sx={{ fontSize: 14 }} />
                  ) : (
                    <AccessTimeIcon sx={{ fontSize: 14 }} />
                  )
                }
                label={showAll ? `All models · ${all.length}` : "Recent"}
                action={{
                  label: showAll ? "Show recent" : "Show all",
                  onClick: () => {
                    setShowAll((v) => !v);
                    setShowAllPinned(false);
                  },
                  testId: "toggle-all-models",
                }}
              />
              <ListBox
                rows={OVERVIEW_LIST_ROWS}
                data-overview-list
                data-testid={showAll ? "all-models-list" : undefined}
              >
                {showAll ? (
                  <ModelRows
                    models={all}
                    freshModelId={freshModelId}
                    showActiveCount
                    movable
                  />
                ) : recent.length > 0 ? (
                  <ModelRows
                    models={recent}
                    freshModelId={freshModelId}
                    movable
                  />
                ) : (
                  <HintRow>Open a model and it&apos;ll show up here.</HintRow>
                )}
              </ListBox>
            </div>
          </div>
        )}
        <CreateModelStrip
          hasModels={!modelsInitialized || hasLiveModels(models)}
          isNaming={!!naming}
          onNewModel={() => {
            setNaming({ spaceId: selectedSpaceId });
            setShowAllPinned(false);
          }}
        />
        <NewModelOverlay
          open={!!naming}
          target={naming ?? { spaceId: null }}
          anchorRef={listsRef}
          paneRef={paneRef}
          createModel={createModel}
          onClose={() => setNaming(null)}
        />
      </div>
      <div className="absolute bottom-4 right-4 z-10">
        <VersionChip />
      </div>
    </div>
  );
};
